import crypto from 'crypto';

/**
 * Generate a unique ID for documents, chunks, etc.
 */
export function generateId(prefix: string = ''): string {
  const timestamp = Date.now().toString(36);
  const random = crypto.randomBytes(6).toString('hex');
  return prefix ? `${prefix}_${timestamp}_${random}` : `${timestamp}_${random}`;
}

/**
 * Sanitize and normalize text content
 */
export function sanitizeText(text: string): string {
  return text
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines
    .replace(/[ \t]{2,}/g, ' ') // Normalize whitespace
    .replace(/^\s+|\s+$/gm, '') // Trim lines
    .trim();
}

/**
 * Extract headers from markdown or HTML content
 */
export function extractHeaders(content: string): string[] {
  const headers: string[] = [];
  
  // Extract markdown headers
  const mdHeaders = content.match(/^#{1,6}\s+(.+)$/gm);
  if (mdHeaders) {
    headers.push(...mdHeaders.map(h => h.replace(/^#+\s+/, '').trim()));
  }
  
  // Extract HTML headers if no markdown headers found
  if (headers.length === 0) {
    const htmlHeaders = content.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi);
    if (htmlHeaders) {
      headers.push(...htmlHeaders.map(h => h.replace(/<[^>]*>/g, '').trim()));
    }
  }
  
  return headers.filter(h => h.length > 0);
}

/**
 * Count tokens in text (rough estimate)
 */
export function estimateTokens(text: string): number {
  // Rough token estimation: ~4 characters per token for English text
  return Math.ceil(text.length / 4);
}

/**
 * Split text while preserving word boundaries
 */
export function splitOnWordBoundary(text: string, maxLength: number): string[] {
  if (text.length <= maxLength) {
    return [text];
  }
  
  const chunks: string[] = [];
  let currentChunk = '';
  const words = text.split(/\s+/);
  
  for (const word of words) {
    const testChunk = currentChunk ? `${currentChunk} ${word}` : word;
    
    if (testChunk.length <= maxLength) {
      currentChunk = testChunk;
    } else {
      if (currentChunk) {
        chunks.push(currentChunk.trim());
        currentChunk = word;
      } else {
        // Handle very long words
        chunks.push(word);
        currentChunk = '';
      }
    }
  }
  
  if (currentChunk) {
    chunks.push(currentChunk.trim());
  }
  
  return chunks.filter(chunk => chunk.length > 0);
}

/**
 * Create overlapping chunks from text
 */
export function createOverlappingChunks(
  text: string,
  chunkSize: number,
  overlapSize: number,
  splitOnWords: boolean = true
): Array<{
  content: string;
  startIndex: number;
  endIndex: number;
}> {
  if (text.length <= chunkSize) {
    return [{
      content: text,
      startIndex: 0,
      endIndex: text.length,
    }];
  }
  
  const chunks: Array<{
    content: string;
    startIndex: number;
    endIndex: number;
  }> = [];
  
  let currentIndex = 0;
  
  while (currentIndex < text.length) {
    const endIndex = Math.min(currentIndex + chunkSize, text.length);
    let chunkText = text.slice(currentIndex, endIndex);
    
    // If splitting on words and not at the end, try to end at word boundary
    if (splitOnWords && endIndex < text.length) {
      const lastSpaceIndex = chunkText.lastIndexOf(' ');
      if (lastSpaceIndex > chunkSize * 0.8) { // Only adjust if we don't lose too much content
        chunkText = chunkText.slice(0, lastSpaceIndex);
      }
    }
    
    chunks.push({
      content: chunkText.trim(),
      startIndex: currentIndex,
      endIndex: currentIndex + chunkText.length,
    });
    
    // Move to next chunk with overlap
    const actualChunkSize = chunkText.length;
    const step = Math.max(actualChunkSize - overlapSize, 1);
    currentIndex += step;
    
    // Break if we've reached the end
    if (currentIndex >= text.length) {
      break;
    }
  }
  
  return chunks.filter(chunk => chunk.content.length > 0);
}

/**
 * Find sentence boundaries in text
 */
export function findSentenceBoundaries(text: string): number[] {
  const boundaries: number[] = [0];
  
  // Simple sentence boundary detection
  const sentenceEnders = /[.!?]+\s+/g;
  let match;
  
  while ((match = sentenceEnders.exec(text)) !== null) {
    boundaries.push(match.index + match[0].length);
  }
  
  if (boundaries[boundaries.length - 1] !== text.length) {
    boundaries.push(text.length);
  }
  
  return boundaries;
}

/**
 * Find paragraph boundaries in text
 */
export function findParagraphBoundaries(text: string): number[] {
  const boundaries: number[] = [0];
  
  // Find double newlines (paragraph breaks)
  const paragraphBreaks = /\n\s*\n/g;
  let match;
  
  while ((match = paragraphBreaks.exec(text)) !== null) {
    boundaries.push(match.index + match[0].length);
  }
  
  if (boundaries[boundaries.length - 1] !== text.length) {
    boundaries.push(text.length);
  }
  
  return boundaries;
}

/**
 * Extract section from markdown content
 */
export function extractSection(content: string, startHeader: string): string | null {
  const lines = content.split('\n');
  const startPattern = new RegExp(`^#+\\s+${escapeRegExp(startHeader)}\\s*$`, 'i');
  
  let startIndex = -1;
  let endIndex = lines.length;
  let startHeaderLevel = 0;
  
  // Find start of section
  for (let i = 0; i < lines.length; i++) {
    if (startPattern.test(lines[i])) {
      startIndex = i;
      startHeaderLevel = (lines[i].match(/^#+/) || [''])[0].length;
      break;
    }
  }
  
  if (startIndex === -1) {
    return null;
  }
  
  // Find end of section (next header of same or higher level)
  for (let i = startIndex + 1; i < lines.length; i++) {
    const headerMatch = lines[i].match(/^(#+)/);
    if (headerMatch && headerMatch[1].length <= startHeaderLevel) {
      endIndex = i;
      break;
    }
  }
  
  return lines.slice(startIndex, endIndex).join('\n');
}

/**
 * Escape special regex characters
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Calculate cosine similarity between two vectors
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error('Vectors must have the same length');
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  if (normA === 0 || normB === 0) {
    return 0;
  }
  
  return dotProduct / (normA * normB);
}

/**
 * Calculate text similarity using simple metrics
 */
export function textSimilarity(text1: string, text2: string): number {
  const words1 = new Set(text1.toLowerCase().split(/\s+/));
  const words2 = new Set(text2.toLowerCase().split(/\s+/));
  
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  
  return intersection.size / union.size; // Jaccard similarity
}

/**
 * Validate that a string is not empty and not just whitespace
 */
export function isValidContent(content: string): boolean {
  return content.trim().length > 0;
}

/**
 * Truncate text to a maximum length while preserving word boundaries
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  
  const truncated = text.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.slice(0, lastSpaceIndex) + '...';
  }
  
  return truncated + '...';
}

/**
 * Clean and normalize query text
 */
export function normalizeQuery(query: string): string {
  return query
    .trim()
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/[^\w\s\-_.?!]/g, '') // Remove special characters except common ones
    .toLowerCase();
}

/**
 * Extract keywords from text
 */
export function extractKeywords(text: string, minLength: number = 3, maxCount: number = 20): string[] {
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length >= minLength);
  
  // Simple frequency-based keyword extraction
  const frequency: Record<string, number> = {};
  words.forEach(word => {
    frequency[word] = (frequency[word] || 0) + 1;
  });
  
  return Object.entries(frequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxCount)
    .map(([word]) => word);
}

/**
 * Format time duration in human-readable format
 */
export function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) {
    return `${milliseconds}ms`;
  }
  
  const seconds = milliseconds / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(2)}s`;
  }
  
  const minutes = seconds / 60;
  return `${minutes.toFixed(2)}m`;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Create a hash of content for deduplication
 */
export function hashContent(content: string): string {
  return crypto
    .createHash('sha256')
    .update(content)
    .digest('hex')
    .slice(0, 16); // Use first 16 characters
}